import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, FileText, ShieldAlert, Plus, Trash2, Target, Building2, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROJECTS, BOARD_STANCES } from "../config/wiseboardConfig";
import type { BoardStance } from "../config/wiseboardConfig";

interface SalesActivationWorkspaceProps {
  caseId: string;
  dbProject: any;
  artifacts: any[];
  onArtifactSaved: () => void;
}

interface TargetAccount {
  id: string;
  rank: number;
  company: string;
  segment: string;
  fitScore: "High" | "Medium" | "Low";
  buyerRoles: string;
  hypothesis: string;
  outreachAngle: string;
}

const EMPTY_ACCOUNT: Omit<TargetAccount, "id" | "rank"> = {
  company: "",
  segment: "",
  fitScore: "Medium",
  buyerRoles: "",
  hypothesis: "",
  outreachAngle: "",
};

export default function SalesActivationWorkspace({ caseId, dbProject, artifacts, onArtifactSaved }: SalesActivationWorkspaceProps) {
  const [p21Stance, setP21Stance] = useState<BoardStance | null>(null);
  const [p21Loading, setP21Loading] = useState(true);
  const [accounts, setAccounts] = useState<TargetAccount[]>([]);
  const [saving, setSaving] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: string; content: string }[]>([]);
  const [aiLoading, setAiLoading] = useState(false);

  // Load P21 stance for gate check
  useEffect(() => {
    const loadP21 = async () => {
      setP21Loading(true);
      const { data: p21Proj } = await supabase
        .from("wiseboard_projects")
        .select("id, status")
        .eq("case_id", caseId)
        .eq("project_type", "P21")
        .single();

      if (p21Proj && p21Proj.status === "approved") {
        const { data: art } = await supabase
          .from("wiseboard_artifacts")
          .select("content")
          .eq("project_id", p21Proj.id)
          .eq("approval_status", "approved")
          .order("version", { ascending: false })
          .limit(1)
          .single();

        if (art?.content) {
          try {
            const parsed = JSON.parse(art.content);
            setP21Stance(parsed.stance || null);
          } catch {
            setP21Stance(null);
          }
        }
      }
      setP21Loading(false);
    };
    loadP21();
  }, [caseId]);

  // Load existing artifact data
  useEffect(() => {
    if (artifacts.length > 0 && artifacts[0].content) {
      try {
        const parsed = JSON.parse(artifacts[0].content);
        if (parsed.accounts) setAccounts(parsed.accounts);
      } catch {
        // not structured
      }
    }
  }, [artifacts]);

  const isBlocked = !p21Stance || p21Stance === "No-Go";
  const gateMessage = p21Loading
    ? "Checking P21 Board Decision..."
    : !p21Stance
      ? "P21 Board Decision has not been approved yet. Complete P21 first."
      : p21Stance === "No-Go"
        ? "P21 stance is No-Go — Sales Activation cannot proceed."
        : null;

  const addAccount = () => {
    setAccounts(prev => [
      ...prev,
      { ...EMPTY_ACCOUNT, id: crypto.randomUUID(), rank: prev.length + 1 },
    ]);
  };

  const updateAccount = (id: string, field: keyof TargetAccount, value: string) => {
    setAccounts(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const removeAccount = (id: string) => {
    setAccounts(prev => prev.filter(a => a.id !== id).map((a, i) => ({ ...a, rank: i + 1 })));
  };

  const saveArtifact = async () => {
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); setSaving(false); return; }

    const latestVersion = artifacts.length > 0 ? Math.max(...artifacts.map(a => a.version)) : 0;
    const payload = { accounts, p21Stance, savedAt: new Date().toISOString() };

    const { data, error } = await supabase.from("wiseboard_artifacts").insert({
      project_id: dbProject.id,
      user_id: user.id,
      name: "P22 — Sales Activation Pack",
      version: latestVersion + 1,
      content: JSON.stringify(payload),
      approval_status: "draft",
    }).select().single();

    if (!error && data) {
      toast.success(`Sales Activation v${data.version} saved (${accounts.length} accounts)`);
      onArtifactSaved();
    }
    setSaving(false);
  };

  const sendToAI = async () => {
    if (!chatInput.trim()) return;
    setAiLoading(true);
    const newMessages = [...chatMessages, { role: "user", content: chatInput }];
    setChatMessages(newMessages);
    setChatInput("");

    const contextBlock = accounts.length > 0
      ? `\n\nCurrent target accounts (${accounts.length}):\n${accounts.map(a => `${a.rank}. ${a.company} — ${a.segment} — Fit: ${a.fitScore} — Roles: ${a.buyerRoles}`).join("\n")}`
      : "";

    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: {
          messages: newMessages,
          model: "google/gemini-2.5-pro",
          systemPrompt: PROJECTS["P22"].systemPrompt + `\n\nP21 Board Stance: ${p21Stance}${contextBlock}`,
        },
      });
      if (error) throw error;
      const reply = data?.choices?.[0]?.message?.content || "No response received.";
      setChatMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch (err: any) {
      toast.error("AI error: " + (err.message || "Unknown"));
    }
    setAiLoading(false);
  };

  // Gate block UI
  if (p21Loading || isBlocked) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="py-12 text-center space-y-3">
          <ShieldAlert className="h-10 w-10 mx-auto text-destructive" />
          <p className="text-sm font-medium">{gateMessage}</p>
          {p21Stance === "No-Go" && (
            <Badge variant="destructive">P21: No-Go</Badge>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Gate status */}
      <Card>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Target className="h-4 w-4 text-primary" />
            <span className="font-medium">P21 Stance:</span>
            <Badge className={
              p21Stance === "Go" ? "bg-green-100 text-green-800" :
              p21Stance === "Pilot" ? "bg-blue-100 text-blue-800" :
              "bg-amber-100 text-amber-800"
            }>
              {p21Stance}
            </Badge>
            <span className="text-muted-foreground">— Sales Activation unlocked</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => {
              if (accounts.length === 0) { toast.error("No accounts to export"); return; }
              const headers = ["Rank","Company","Segment","Fit Score","Buyer Roles","Commercial Hypothesis","Outreach Angle"];
              const rows = accounts.map(a => [a.rank, a.company, a.segment, a.fitScore, a.buyerRoles, a.hypothesis, a.outreachAngle].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
              const csv = [headers.join(","), ...rows].join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `Sales_Activation_${accounts.length}_accounts.csv`;
              link.click();
              URL.revokeObjectURL(url);
              toast.success("CSV downloaded");
            }} disabled={accounts.length === 0}>
              <Download className="h-3.5 w-3.5 mr-1" />CSV
            </Button>
            <Button size="sm" variant="outline" onClick={saveArtifact} disabled={saving}>
              <FileText className="h-3.5 w-3.5 mr-1" />{saving ? "Saving..." : `Save (${accounts.length} accounts)`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Target Account Table */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Target Accounts ({accounts.length}/20)
              </CardTitle>
              <Button size="sm" variant="outline" onClick={addAccount} disabled={accounts.length >= 20}>
                <Plus className="h-3.5 w-3.5 mr-1" />Add
              </Button>
            </div>
            <CardDescription className="text-xs">Priority target accounts with buyer-role mapping and outreach logic</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[calc(100vh-20rem)] overflow-auto">
            {accounts.length === 0 && (
              <div className="text-center py-8 text-xs text-muted-foreground">
                No accounts yet. Add manually or ask the AI to suggest target accounts.
              </div>
            )}
            {accounts.map(account => (
              <div key={account.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[10px]">#{account.rank}</Badge>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeAccount(account.id)}>
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Company name"
                    value={account.company}
                    onChange={e => updateAccount(account.id, "company", e.target.value)}
                    className="text-xs h-8"
                  />
                  <Input
                    placeholder="Segment"
                    value={account.segment}
                    onChange={e => updateAccount(account.id, "segment", e.target.value)}
                    className="text-xs h-8"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={account.fitScore} onValueChange={v => updateAccount(account.id, "fitScore", v)}>
                    <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High Fit</SelectItem>
                      <SelectItem value="Medium">Medium Fit</SelectItem>
                      <SelectItem value="Low">Low Fit</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Buyer roles (e.g. CTO, VP Eng)"
                    value={account.buyerRoles}
                    onChange={e => updateAccount(account.id, "buyerRoles", e.target.value)}
                    className="text-xs h-8"
                  />
                </div>
                <Input
                  placeholder="Commercial hypothesis"
                  value={account.hypothesis}
                  onChange={e => updateAccount(account.id, "hypothesis", e.target.value)}
                  className="text-xs h-8"
                />
                <Input
                  placeholder="Outreach angle / priority rationale"
                  value={account.outreachAngle}
                  onChange={e => updateAccount(account.id, "outreachAngle", e.target.value)}
                  className="text-xs h-8"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* AI Chat */}
        <Card className="flex flex-col max-h-[calc(100vh-16rem)]">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm">AI Lead Builder</CardTitle>
            <CardDescription className="text-xs">
              Uses P22 system prompt with P21 stance context
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto space-y-3 mb-3 min-h-[200px]">
              {chatMessages.length === 0 && (
                <div className="text-xs text-muted-foreground italic p-4 text-center">
                  {PROJECTS["P22"].helpPrompt}
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`text-sm rounded-lg p-3 ${msg.role === "user" ? "bg-primary/10 ml-8" : "bg-muted mr-8"}`}>
                  <div className="font-medium text-xs mb-1 text-muted-foreground">{msg.role === "user" ? "You" : "AI"}</div>
                  <div className="whitespace-pre-wrap text-xs">{msg.content}</div>
                </div>
              ))}
              {aiLoading && <div className="text-xs text-muted-foreground animate-pulse">AI is building leads...</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Ask the AI to suggest accounts, map buyers..."
                rows={2}
                className="text-sm"
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendToAI(); } }}
              />
              <Button size="icon" onClick={sendToAI} disabled={aiLoading || !chatInput.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Version history */}
      {artifacts.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {artifacts.map(a => (
            <Badge key={a.id} variant="outline" className="text-[10px] cursor-pointer" onClick={() => {
              try {
                const parsed = JSON.parse(a.content);
                if (parsed.accounts) setAccounts(parsed.accounts);
              } catch { /* ignore */ }
            }}>
              v{a.version} — {a.approval_status}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
